import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { INSIGHTS } from "../../data/content";

const TINTS = ["ground-lab", "ground-highlighter", "ground-margin"];

// Read time = words in body / ~230 wpm, rounded up to nearest minute.
function estimateReadTime(article) {
  const words = (article.body || []).reduce((a, p) => a + p.split(/\s+/).length, 0);
  const mins = Math.max(2, Math.round(words / 230));
  return `${mins} min read`;
}

// Static publish date for now — should come from real CMS in future.
const PUBLISH_DATES = {
  "best-analyst-loses-room": "Oct 2025",
  "voice-gap": "Nov 2025",
  "ai-makes-valuable": "Nov 2025"
};

export default function InsightsSection({ compact = false }) {
  return (
    <section data-testid="insights-section" className="ground-paper sec-pad border-t border-[color:var(--line-soft)]">
      <div className="wrap">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <p className="stamp mb-4" style={{ fontSize: 12 }}>Research &amp; Field Notes</p>
            <h2 className="h-section" style={{ fontSize: "clamp(28px, 3.4vw, 46px)", lineHeight: 1.08 }}>
              Working notes on human capability
              <br />
              <span className="claim">in the age of AI.</span>
            </h2>
          </div>
          {!compact && (
            <Link to="/insights" className="arrow-link" data-testid="insights-view-all-top">
              All notes <ArrowUpRight size={14} />
            </Link>
          )}
        </div>

        <div className="border-t-[1.5px] border-[color:var(--ink)] grid grid-cols-1 md:grid-cols-3">
          {INSIGHTS.map((a, i) => {
            const readTime = estimateReadTime(a);
            const date = PUBLISH_DATES[a.slug] || "2025";
            return (
              <Link
                key={a.slug}
                to={`/insights/${a.slug}`}
                data-testid={`insight-card-${a.slug}`}
                className={`group ${TINTS[i % TINTS.length]} p-8 md:p-10 border-b-[1.5px] border-[color:var(--ink)] md:border-b-0 ${i > 0 ? "md:border-l-[1.5px]" : ""}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-baseline gap-3">
                    <span className="stamp" style={{ fontSize: 12 }}>{a.category}</span>
                    <span className="stamp-dim" style={{ fontSize: 11 }}>{date}</span>
                  </div>
                  <ArrowUpRight
                    size={22}
                    className="text-[#0E1330] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1"
                  />
                </div>

                <h3 className="font-hook mt-8" style={{ fontSize: 24, lineHeight: 1.1, letterSpacing: "-0.015em" }}>
                  {a.title}
                </h3>
                <p className="font-body-serif mt-4" style={{ fontSize: 16, lineHeight: 1.6 }}>
                  {a.excerpt}
                </p>

                <div className="mt-10 pt-4 border-t border-[color:var(--line-soft)] flex flex-wrap items-center gap-x-6 gap-y-1">
                  <span className="stamp-dim" style={{ fontSize: 11 }}>{readTime}</span>
                  <span className="stamp-dim" style={{ fontSize: 11 }}>Evidence: interpretive</span>
                </div>
              </Link>
            );
          })}
        </div>

        {!compact && (
          <div className="mt-12 flex justify-center">
            <Link to="/insights" className="btn-outline" data-testid="insights-view-all-bottom">
              Read all field notes <ArrowUpRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
